import moment from 'moment';
import jsSHA from 'jssha';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { LocalStorageUtils } from '@/utils';

export const BACKEND_URL = import.meta.env.VITE_APP_BACKEND_URL;

/*
 * Request signing assumes this is a bare origin, and nothing downstream re-checks it.
 *
 * `signPath` signs `/${uri}` verbatim; `signingString` reaches the same line by chopping scheme and
 * host off an absolute URL. Identical for `https://host`, which is what `.env.base` documents and
 * what every deployment sets. Give this a path prefix and the two stop agreeing with each other as
 * well as with the API — `/pump/...` from one, `/v1/pump/...` from the other — and a failed HMAC
 * comes back as a bare 401 that names nothing. Loud here rather than unexplained there.
 *
 * Reported, not thrown: the configuration is correct today and a throw at module load would take
 * the app down over it. `console` rather than `@/errors`, which reaches the router and would pull
 * this module into its import graph.
 */
if (BACKEND_URL && new URL(BACKEND_URL, window.location.origin).pathname !== '/') {
  // eslint-disable-next-line no-console
  console.error('[api] VITE_APP_BACKEND_URL carries a path, and signing assumes a bare origin. ' +
    'Every signed request and the socket handshake will sign a request line the API cannot verify.',
  { backendUrl: BACKEND_URL });
}

/**
 * The shared client. Interceptors are installed from `main.ts` via `installHttpErrorInterceptor`
 * rather than here, to keep this module off `@/errors`'s import graph (which reaches the router).
 *
 * Not everything goes through here: `studyService` and `formService` call bare `axios` for FormData
 * and presigned-S3 uploads.
 */
export const apiClient = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
});

/**
 * The subset of the axios surface a signed request needs. Deliberately not `AxiosInstance`: a signed
 * client carries credentials for one request line, so exposing `defaults` would re-open the door to
 * setting them globally.
 *
 * `T` defaults to `any` to match `AxiosInstance`'s own signatures. Narrowing it to `unknown` here
 * would be more truthful but would land as a type error at roughly forty `response.data` reads
 * across the service layer, none of which this change is about.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface SignedApiClient {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  post: <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  patch: <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
  put: <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * The credential headers for one request line.
 *
 * Exported for the one caller that cannot go through axios at all: `metricsService` sends its
 * unload metric with `fetch(..., { keepalive: true })`, which axios has no equivalent for.
 *
 * No cached user means nothing to sign as — send the request unsigned rather than half-signed.
 */
export const signedHeaders = (method: string, uri: string): Record<string, string> => {
  if (!LocalStorageUtils.getUserKeys()) {
    return {};
  }
  const { date, string } = signPath(method, `/${uri}`);
  return { 'X-Date': date, Authorization: string };
};

/**
 * The request line the socket handshake signature covers.
 *
 * A socket.io handshake is really a GET to `/socket.io/`, so this is a convention rather than a
 * description — but it is the convention the API verifies against. `SOCKET_SIGNING_PATH` in
 * `ts-api/src/socket/socket.principal.service.ts` is the other half; change one and change the
 * other.
 */
const SOCKET_SIGNING_URI = 'socket';

/**
 * The credential a socket handshake carries, for `io(url, { auth })`.
 *
 * The API disconnects a handshake it cannot verify, so an unauthenticated socket is no longer a
 * connected one. Returning `{}` with no cached user is deliberate: an anonymous connection should
 * be refused by the server rather than faked here.
 *
 * Signed fresh on every call, and `socket.ts` passes it as a callback so a reconnect re-signs
 * rather than replaying the date from the first attempt.
 */
export const socketAuth = (): Record<string, string> => {
  const credentials = signedHeaders('GET', SOCKET_SIGNING_URI);
  if (!credentials.Authorization) {
    return {};
  }

  return { authorization: credentials.Authorization, date: credentials['X-Date'] };
};

/**
 * A client that signs one request line, as per-request headers on the shared instance.
 *
 * The signature covers `method + uri`, so it is valid for exactly the call it was built for. It used
 * to be written onto `apiClient.defaults.headers.common` and the singleton returned, which meant
 * every later request through the bare client carried whichever signature was set last — including
 * `DeepLinkService.getDeepLinkData`, which posts to Branch.io and had to `delete` those two headers
 * by hand to avoid handing our HMAC to a third party. Per-request headers remove the leak and the
 * two hand-deletes, and let a caller build a signed client before it sends.
 *
 * The shared instance is still what sends, so `installHttpErrorInterceptor` continues to apply.
 */
export const signedApiClient = (method: string, uri: string): SignedApiClient => {
  const credentials = signedHeaders(method, uri);

  const signed = (config?: AxiosRequestConfig): AxiosRequestConfig => ({
    ...config,
    headers: { ...config?.headers, ...credentials }
  });

  return {
    get: (url, config) => apiClient.get(url, signed(config)),
    delete: (url, config) => apiClient.delete(url, signed(config)),
    post: (url, data, config) => apiClient.post(url, data, signed(config)),
    patch: (url, data, config) => apiClient.patch(url, data, signed(config)),
    put: (url, data, config) => apiClient.put(url, data, signed(config))
  };
};

interface SigningObject {
  date: string;
  string: string;
}

const hexToBase64 = (hexstring: string): string => {
  const processedHex: string = hexstring
    .match(/\w{2}/g)!
    .map((a: string) => {
      return String.fromCharCode(parseInt(a, 16));
    })
    .join('');
  return btoa(processedHex);
};

const hexToArrayBuffer = (hex: string): Uint8Array => {
  const view = new Uint8Array(hex.length / 2);

  for (let i = 0; i < hex.length; i += 2) {
    view[i / 2] = parseInt(hex.substring(i, i + 2), 16);
  }

  return view;
};

const stringToArrayBuffer = (str: string): Uint8Array => {
  const enc = new TextEncoder(); // always utf-8
  return enc.encode(str);
};

/**
 * The signature over one request line, for a path that is already server-relative.
 *
 * `signedHeaders` used to reach this through `signingString`, handing it `${BACKEND_URL}/${uri}`
 * so the origin could be chopped straight back off. That round trip put the base URL in the
 * signature's way for no gain: it returns `/${uri}` only while `BACKEND_URL` is a bare origin, and
 * silently returns something else when it is not. Unset — which is every unit test, since the
 * `.env` carrying it is untracked — it signed `/` for `socket` and `/abc/perfusion-dashboard` for
 * `pump/case/abc/perfusion-dashboard`. Nothing rejects a wrong path here; the API just fails the
 * HMAC, so it surfaces as a 401 or a socket that connects and is dropped.
 *
 * Signing `/${uri}` directly is byte-identical for every base URL the app is actually configured
 * with, and stops the value mattering at all.
 */
const signPath = (method: string, path: string): SigningObject => {
  let keyData: string = '';
  const localKeys = LocalStorageUtils.getUserKeys();
  if (localKeys) {
    keyData = localKeys.data as string;
  }
  const b64KeyUri = hexToBase64(localKeys?.key ?? '');
  const date = moment().utc().format('ddd, D MMM YYYY HH:mm:ss [GMT]');
  const shaObj = new jsSHA('SHA-256', 'ARRAYBUFFER');
  shaObj.setHMACKey(hexToArrayBuffer(keyData as string), 'ARRAYBUFFER');
  shaObj.update(stringToArrayBuffer(method + ' ' + path + ' HTTP/1.1\ndate: ' + date));
  const b64Hmac = shaObj.getHMAC('B64');

  return {
    date: date,
    string: `Signature keyId="${b64KeyUri}",algorithm="hmac-sha256",headers="request-line date",signature="${b64Hmac}"`
  };
};

/**
 * The same, for the callers that hold an absolute URL rather than a URI: `studyService`'s two
 * multipart uploads, which post through bare `axios` and build the full URL before signing it.
 */
export const signingString = (
  method: string,
  url: string
): SigningObject => signPath(method, '/' + url.split('/').slice(3).join('/'));
